import { useState, useMemo } from 'react';
import { isEqual } from 'lodash-es';

import { SelectChangeEvent, Theme, useMediaQuery } from '@mui/material';

import {
  ApplyingCollegeWithSchool,
  DeadlineTypeOption,
  IpedsCardInfo,
  DeadlineType,
  ApplicationType,
} from '@common-types';

import useEducationPlannerStore from '@store/EducationPlannerStore';
import useApplyingSchoolsPlannerStore from '@store/ApplyingSchoolsPlannerStore';

import { baseService } from '@services/base.service';

import { useDebouncedEffect } from '@hooks/useDebouncedEffect';

import { ORDER_BY_LATEST } from '@constants';
import { INITIAL_PAGE_FILTERS, MAX_SCHOOL_COUNT } from '@constants/applyingSchoolsPlanner';
import { ALL_TYPES_FILTER_VALUE } from '@constants/educationPlanner';

import { EmptyStateText } from '@components/EmptyStateText';
import ActionButton from '@components/ActionButton';

import NoResultsIcon from '@images/no-results.svg?react';
import PlusIcon from '@images/plus-button.svg?react';

import PlannerSchoolCard from '../PlannerSchoolCard';
import SchoolSelection from '../../../EducationPlanner/components/SchoolSelectionPlanner';

import { Filters } from './Filters';

import {
  StyledContentSchoolList,
  StyledContentSchoolListHeader,
  StyledContentSchoolListItem,
} from './EducationPlannerSchoolList.styles';
import { useAnyCommonAppEnabled } from '@hooks/useAnyCommonAppEnabled';

interface CardDeadlineTypeOptions {
  id: number;
  deadlineTypeOptions: DeadlineTypeOption[];
}

export const EducationPlannerSchoolList = () => {
  const { anyTenantHasCommonApp } = useAnyCommonAppEnabled();

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [isShowFilters, setIsShowFilters] = useState(!isMobileScreen);
  const [cardId, setExtendedCardId] = useState<number | undefined>(undefined);
  const [isOpenSchoolSelection, setIsOpenSchoolSelection] = useState(false);

  const { appliedSchools, deleteAppliedSchool, pageFilters, orderBy, setPageFilters, setOrderBy } =
    useApplyingSchoolsPlannerStore((state) => state);

  const resetSchoolSelectionData = useEducationPlannerStore((state) => state.resetSchoolSelectionData);

  const handleAddSchool = () => {
    setIsOpenSchoolSelection(true);
  };

  const handleRemoveSchool = async (id: number) => {
    await deleteAppliedSchool(id);
  };

  const handleShowFilters = () => {
    setIsShowFilters(!isShowFilters);
  };

  const handleCloseSchoolSelection = () => {
    resetSchoolSelectionData();
    setIsOpenSchoolSelection(false);
  };

  const handleFilterChange = (filterName: string) => (event: SelectChangeEvent<unknown>) => {
    setPageFilters({ [filterName]: event.target.value });
  };

  const handleOrderByChange = (event: SelectChangeEvent<unknown>) => {
    setOrderBy(event.target.value as string);
  };

  const handleExtendSchool = (id: number | undefined) => {
    setExtendedCardId(id);
  };

  const filteredSchools = useMemo(() => {
    return appliedSchools.reduce((acc: ApplyingCollegeWithSchool[], school: ApplyingCollegeWithSchool) => {
      const isApplicationTypeAll = pageFilters.applicationType === ALL_TYPES_FILTER_VALUE;
      const isApplicationStatus = pageFilters.applicationStatus === ALL_TYPES_FILTER_VALUE;

      const isApplicationTypeEqual =
        school.application?.type === pageFilters.applicationType ||
        (!school.application?.type && pageFilters.applicationType === ApplicationType.INSTITUTIONAL);
      const isApplicationStatusEqual = school.application?.status === pageFilters.applicationStatus;

      if (!isApplicationTypeAll && !isApplicationStatus && isApplicationTypeEqual && isApplicationStatusEqual) {
        return [...acc, school];
      }

      if (!isApplicationTypeAll && isApplicationStatus && isApplicationTypeEqual) {
        return [...acc, school];
      }

      if (isApplicationTypeAll && !isApplicationStatus && isApplicationStatusEqual) {
        return [...acc, school];
      }

      return acc;
    }, [] as ApplyingCollegeWithSchool[]);
  }, [pageFilters, appliedSchools]);

  const renderSchoolArr = isEqual(INITIAL_PAGE_FILTERS, pageFilters) ? appliedSchools : filteredSchools;

  const sortedSchools = useMemo(() => {
    const schoolsWithDeadline = [] as ApplyingCollegeWithSchool[];
    const schoolsWithoutDeadline = [] as ApplyingCollegeWithSchool[];

    renderSchoolArr.forEach((school) => {
      if (school.application?.deadline) {
        schoolsWithDeadline.push(school);
      } else {
        schoolsWithoutDeadline.push(school);
      }
    });

    const sortedSchoolsWithDeadline = schoolsWithDeadline.sort(
      (a, b) => Date.parse(a.application?.deadline as string) - Date.parse(b.application?.deadline as string),
    );

    return [
      ...(orderBy === ORDER_BY_LATEST ? sortedSchoolsWithDeadline.reverse() : sortedSchoolsWithDeadline),
      ...schoolsWithoutDeadline,
    ];
  }, [renderSchoolArr, orderBy]);

  const [deadlineTypeOptions, setDeadlineTypeOptions] = useState<CardDeadlineTypeOptions[]>([]);

  useDebouncedEffect(
    () => {
      const fetchCardInfo = async () => {
        const ids = sortedSchools.filter((school) => !!school.id).map((school) => school.id);

        if (ids.length === 0) {
          return;
        }

        const cardInfo = await baseService.getAsync<IpedsCardInfo[]>(`/card-info?id=${ids.join(',')}`);
        const deadlineTypeOptions = cardInfo.map((info) => {
          const options =
            info.deadlineTypeOptions.length > 0
              ? [...info.deadlineTypeOptions, { deadlineType: DeadlineType.NOT_SURE, deadline: '' }]
              : [];

          return {
            id: info.id,
            deadlineTypeOptions: options,
          };
        });

        setDeadlineTypeOptions(deadlineTypeOptions);
      };

      void fetchCardInfo();
    },
    1000,
    [sortedSchools],
  );

  return (
    <StyledContentSchoolList>
      <StyledContentSchoolListHeader>
        {!isMobileScreen ? (
          <Filters
            pageFilters={pageFilters}
            handleFilterChange={handleFilterChange}
            handleOrderByChange={handleOrderByChange}
          />
        ) : null}
        <ActionButton
          data-testid="addSchool"
          disabled={appliedSchools.length >= MAX_SCHOOL_COUNT}
          onClick={handleAddSchool}
          startIcon={<PlusIcon />}
        >
          {anyTenantHasCommonApp ? 'Add non-Common App member schools' : 'Add school'}
        </ActionButton>
        {isMobileScreen && (
          <ActionButton onClick={handleShowFilters} variant="outlined">
            {isShowFilters ? 'Close' : 'Filters'}
          </ActionButton>
        )}
        {isMobileScreen && isShowFilters ? (
          <Filters
            pageFilters={pageFilters}
            handleFilterChange={handleFilterChange}
            handleOrderByChange={handleOrderByChange}
          />
        ) : null}
      </StyledContentSchoolListHeader>
      {sortedSchools.length === 0 && (
        <StyledContentSchoolListItem>
          <EmptyStateText icon={<NoResultsIcon />} title="No results" />
        </StyledContentSchoolListItem>
      )}
      {sortedSchools?.map((school) => (
        <PlannerSchoolCard
          key={school.memberId ?? school.id}
          applyingSchool={school}
          isExtended={school.memberId ? school.memberId === cardId : school.id === cardId}
          deadlineTypeOptions={
            deadlineTypeOptions.find((option) => option.id === school.memberId)?.deadlineTypeOptions ?? []
          }
          onRemoveSchool={handleRemoveSchool}
          onExtendSchool={handleExtendSchool}
        />
      ))}
      {isOpenSchoolSelection && (
        <SchoolSelection
          isOpenSchoolSelection={isOpenSchoolSelection}
          onCloseSchoolSelection={handleCloseSchoolSelection}
        />
      )}
    </StyledContentSchoolList>
  );
};
